import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Button, Chip, IconButton } from '@material-ui/core';
import {
  Cached, ChevronLeft, ChevronRight, Clear, EditOutlined,
} from '@material-ui/icons';
import {
  compose, findIndex, first, get, getOr, map, reject,
} from 'lodash/fp';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '../../../common/components';
import {
  formatPhoneNumber, getStyleValue, isFirstElement, isLastElement,
} from '../../../common/utils';

const S = {
  Dialog: styled(Dialog)`
    .paper {
      position: relative;
      overflow: visible;
      width: 600px;
      padding: 32px 40px 24px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    margin-bottom: 24px;
  `,
  Heading: styled.h3`
    min-height: 30px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  DialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  InfoList: styled.ul`
    ${({ margin }) => margin && css`
      margin-top: ${getStyleValue(margin.top)};
      margin-right: ${getStyleValue(margin.right)};
      margin-bottom: ${getStyleValue(margin.bottom)};
      margin-left: ${getStyleValue(margin.left)};
    `}
  `,
  InfoItem: styled.li`
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  `,
  InfoLabel: styled.p`
    width: 100%;
    max-width: 72px;
    margin-right: 24px;
  `,
  InfoValue: styled.div`
    display: flex;
    align-items: center;
    overflow: auto;
  `,
  Chip: styled(({ error, secondary, ...otherProps }) => <Chip {...otherProps} />)`
    height: auto;
    padding: 0.5px 6px;
    border-radius: 4px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.blueGrey[600]};
    font-size: 0.75rem;
    font-weight: 500;
    text-align: center;
    .MuiChip-label {
      padding: 0;
    }
    :not(:last-child) {
      margin-right: 8px;
    }
    ${({ secondary }) => secondary && css`
      background-color: ${({ theme }) => theme.palette.secondary.main};
    `}
  `,
  Divider: styled.div`
    width: 100%;
    height: 1px;
    margin-bottom: 24px;
    background-color: ${({ theme }) => theme.palette.grey[300]};
  `,
  Row: styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  `,
  InfoLegend: styled.h4`
    width: 100%;
    max-width: 72px;
    margin-right: 24px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 700;
    line-height: 1.54;
  `,
  Note: styled.p`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  DialogActions: styled(DialogActions)`
    display: flex;
  `,
  Buttons: styled.div`
    position: absolute;
    top: 29px;
    right: 29px;
    :not(:last-child) {
      margin-right: 8px;
    }
  `,
  EditOutlined: styled(EditOutlined)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 2rem;
  `,
  Clear: styled(Clear)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 2rem;
  `,
  LeftButton: styled(IconButton)`
    position: absolute;
    top: 50%;
    left: 0;
    transform: translate(-100%, -50%);
    padding: 0;
  `,
  RightButton: styled(IconButton)`
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate(100%, -50%);
    padding: 0;
  `,
  SwapButton: styled(Button)`
    height: 40px;
    min-width: 90px;
    min-height: 40px;
    margin-right: auto;
    padding: 0 16px;
  `,
  Button: styled(Button)`
    height: 40px;
    min-width: 90px;
    min-height: 40px;
    :disabled {
      color: ${({ theme }) => theme.palette.primary.contrastText};
      background-color: ${({ theme }) => theme.palette.grey[400]};
    }
  `,
  Cached: styled(Cached)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.25rem;
  `,
  ChevronLeft: styled(ChevronLeft)`
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 96px;
  `,
  ChevronRight: styled(ChevronRight)`
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 96px;
  `,
};

const ContactDetailDialog = ({
  contact,
  contacts,
  initContact,
  initUpdateTask,
  updateTask,
  open,
  contactEditOpen,
  onClose,
  onContactEditOpen,
  onEnter,
  onFetch,
  onSubmit,
}) => {
  const [exchanged, setExchanged] = useState(false);
  const [prevContact, setPrevContact] = useState(null);

  const groups = useMemo(() => ({
    clazzGroup: get('class_group', contact),
    customGroups: get('custom_groups', contact),
  }), [contact]);
  const title = useMemo(() => (groups.clazzGroup
    ? `${groups.clazzGroup.grade}학년 ${groups.clazzGroup.class_name}반${contact.number ? ` ${get('number', contact)}번` : ''} ${get('name', contact)}`
    : `${get('name', first(groups.customGroups)) ? `${getOr('', 'name', first(groups.customGroups))} ` : ''}${getOr('', 'name', contact)}`), [contact, groups]);

  const showPrevContact = useCallback(() => {
    onFetch({ cid: get(`rows[${[findIndex(c => c.id === contact.id, get('rows', contacts)) - 1]}].id`, contacts) });
  }, [contact, contacts, onFetch]);
  const showNextContact = useCallback(() => {
    onFetch({ cid: get(`rows[${[findIndex(c => c.id === contact.id, get('rows', contacts)) + 1]}].id`, contacts) });
  }, [contact, contacts, onFetch]);
  const openEditDialog = useCallback(() => {
    onClose();

    onContactEditOpen();
  }, [onClose, onContactEditOpen]);
  const exchangeGuardianContacts = useCallback(() => setExchanged(prevExchanged => !prevExchanged), []);
  const handleExited = useCallback(() => {
    setExchanged(false);

    if (!contactEditOpen) {
      initContact();
    }
  }, [contactEditOpen, initContact]);
  const handleClick = useCallback(() => {
    if (get('secondary_guardian_contact', contact)) {
      onSubmit({
        primaryGuardian: get('primary_guardian_contact.id', contact),
        secondaryGuardian: get('secondary_guardian_contact.id', contact),
      });
    }
  }, [contact, onSubmit]);

  useEffect(() => {
    if (updateTask.data) {
      setExchanged(false);

      initUpdateTask();
    }
  }, [initUpdateTask, updateTask.data]);

  if (contact !== prevContact) {
    setExchanged(false);

    setPrevContact(contact);
  }

  return (
    <S.Dialog open={open} onClose={onClose} onEnter={onEnter} onExited={handleExited}>
      <S.DialogTitle>
        <S.Heading>
          {title}
        </S.Heading>
      </S.DialogTitle>
      <S.DialogContent>
        <S.InfoList margin={{ bottom: 24 }}>
          <S.InfoItem>
            <S.InfoLabel id="groups">소속 그룹</S.InfoLabel>
            <S.InfoValue aria-labelledby="groups">
              {groups.clazzGroup && (
                <S.Chip
                  label={`${get('grade', groups.clazzGroup)}학년 ${get('class_name', groups.clazzGroup)}반`}
                  secondary
                />
              )}
              {compose(
                map(customGroup => (
                  <S.Chip key={customGroup.id} label={customGroup.name} />
                )),
                reject(customGroup => customGroup.is_unspecified_group),
                get('custom_groups'),
              )(contact)}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel id="studentPhoneNumber">휴대폰번호</S.InfoLabel>
            <S.InfoValue aria-labelledby="studentPhoneNumber">
              {formatPhoneNumber(get('mobile', contact))}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel id="studentEmail">이메일</S.InfoLabel>
            <S.InfoValue aria-labelledby="studentEmail">
              {get('email', contact) || '-'}
            </S.InfoValue>
          </S.InfoItem>
        </S.InfoList>
        <S.Divider />
        <S.Row>
          <S.InfoLegend>
            대표 보호자
          </S.InfoLegend>
          <S.Note>
            가정통신문은 대표 보호자에게 전송됩니다.
          </S.Note>
        </S.Row>
        <S.InfoList margin={{ bottom: 32 }}>
          <S.InfoItem>
            <S.InfoLabel id="primaryGuardianName">이름</S.InfoLabel>
            <S.InfoValue aria-labelledby="primaryGuardianName">
              {getOr('-', `${exchanged ? 'secondary' : 'primary'}_guardian_contact.name`, contact)}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel id="primaryGuardianName">휴대폰번호</S.InfoLabel>
            <S.InfoValue aria-labelledby="primaryGuardianName">
              {formatPhoneNumber(get(`${exchanged ? 'secondary' : 'primary'}_guardian_contact.mobile`, contact))}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel id="primaryGuardianEmail">이메일</S.InfoLabel>
            <S.InfoValue aria-labelledby="primaryGuardianEmail">
              {get(`${exchanged ? 'secondary' : 'primary'}_guardian_contact.email`, contact) || '-'}
            </S.InfoValue>
          </S.InfoItem>
        </S.InfoList>
        <S.Row>
          <S.InfoLegend>
            보조 보호자
          </S.InfoLegend>
        </S.Row>
        <S.InfoList margin={{ bottom: 40 }}>
          <S.InfoItem>
            <S.InfoLabel id="secondaryGuardianName">이름</S.InfoLabel>
            <S.InfoValue aria-labelledby="secondaryGuardianName">
              {getOr('-', `${exchanged ? 'primary' : 'secondary'}_guardian_contact.name`, contact)}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel id="secondaryGuardianName">휴대폰번호</S.InfoLabel>
            <S.InfoValue aria-labelledby="secondaryGuardianName">
              {formatPhoneNumber(get(`${exchanged ? 'primary' : 'secondary'}_guardian_contact.mobile`, contact))}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel id="secondaryGuardianEmail">이메일</S.InfoLabel>
            <S.InfoValue aria-labelledby="secondaryGuardianEmail">
              {get(`${exchanged ? 'primary' : 'secondary'}_guardian_contact.email`, contact) || '-'}
            </S.InfoValue>
          </S.InfoItem>
        </S.InfoList>
      </S.DialogContent>
      <S.DialogActions>
        <S.Buttons>
          <IconButton type="button" size="small" onClick={openEditDialog}>
            <S.EditOutlined />
          </IconButton>
          <IconButton type="button" size="small" onClick={onClose}>
            <S.Clear />
          </IconButton>
        </S.Buttons>
        {get('secondary_guardian_contact', contact) && (
          <S.SwapButton type="button" onClick={exchangeGuardianContacts}>
            <S.Cached />
            대표 보호자 변경
          </S.SwapButton>
        )}
        <S.Button type="button" variant="outlined" onClick={onClose}>
          취소
        </S.Button>
        <S.Button
          type="button"
          color="primary"
          variant="contained"
          disabled={!exchanged}
          onClick={handleClick}
        >
          저장
        </S.Button>
      </S.DialogActions>
      {get('id', contact) && !isFirstElement(c => c.id === get('id', contact), get('rows', contacts)) && (
        <S.LeftButton type="button" size="small" onClick={showPrevContact}>
          <S.ChevronLeft />
        </S.LeftButton>
      )}
      {get('id', contact) && !isLastElement(c => c.id === get('id', contact), get('rows', contacts)) && (
        <S.RightButton type="button" size="small" onClick={showNextContact}>
          <S.ChevronRight />
        </S.RightButton>
      )}
    </S.Dialog>
  );
};

ContactDetailDialog.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  contacts: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    total: PropTypes.string,
    perPage: PropTypes.string,
  }),
  initContact: PropTypes.func.isRequired,
  initUpdateTask: PropTypes.func.isRequired,
  updateTask: PropTypes.shape({ data: null, loading: false, error: null }).isRequired,
  open: PropTypes.bool.isRequired,
  contactEditOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onContactEditOpen: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  onFetch: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

ContactDetailDialog.defaultProps = {
  contact: null,
  contacts: null,
};


export default ContactDetailDialog;
